import { defineComponent as _defineComponent } from 'vue'
import { ref, computed } from "vue"
import useRouter from "@/router/useRouter"
import ConsultantInvoiceDetail from "@/modules/Invoicing/components/ConsultantInvoiceDetail.vue"
import {
  ConsultantInvoice,
  InvoiceDetailQuery,
  GrantApprovalMutation,
  GrantApprovalMutationMutation,
  GrantApprovalMutationMutationVariables,
  MarkConsultantInvoiceAsPaidMutation,
  InvoiceDetailQueryQuery,
  InvoiceDetailQueryQueryVariables,
  MarkConsultantInvoiceAsPaidMutationMutation,
  MarkConsultantInvoiceAsPaidMutationMutationVariables,
  MarkConsultantInvoiceAsProcessingMutation,
  MarkConsultantInvoiceAsProcessingMutationMutation,
  MarkConsultantInvoiceAsProcessingMutationMutationVariables,
  ArchiveConsultantInvoiceMutation,
  ArchiveConsultantInvoiceMutationMutation,
  ArchiveConsultantInvoiceMutationMutationVariables,
} from "@/gql"
import { useQuery } from "@vue/apollo-composable"
import { PageState } from "@/constants"
import { usePageState } from "@/hooks/usePageState"
import PageStateError from "@/components/widgets/common/PageStateError.vue"
import Icon from "@/components/widgets/icons/Icon.vue"
import { useMutation } from "@/hooks/useMutation"
import { useNotification } from "@/hooks/useNotification"
import AppDetailPage from "@/components/layout/AppDetailPage.vue"
import { useTranslation } from "@/i18n/useTranslation"


export default /*#__PURE__*/_defineComponent({
  __name: 'InvoiceDetail',
  setup(__props) {

const { t } = useTranslation("Missions/Invoices")

const { router } = useRouter()
const { mutate } = useMutation()

const pageState = ref(PageState.Loading)
const isSubmitting = ref(false)
const showDialog = ref(false)
const invoiceAction = ref<keyof typeof actions | null>(null)

const actions = {
  approve: "approve",
  markAsPaid: "markAsPaid",
  markAsProcessing: "markAsProcessing",
  archive: "archive",
} as const

const onOpenDialog = (action: keyof typeof actions) => {
  showDialog.value = true
  invoiceAction.value = action
}

const { addSuccess, addError } = useNotification()

const { handlePageErrorState } = usePageState(pageState)

const { loading, result, onError } = useQuery<
  InvoiceDetailQueryQuery,
  InvoiceDetailQueryQueryVariables
>(InvoiceDetailQuery, {
  filter: { idIn: [`ConsultantInvoice:${router.currentRoute.params.id}`] },
})

const invoice = computed(() => result.value?.consultantInvoices.data[0] as ConsultantInvoice)

onError((error) => {
  handlePageErrorState(error)
})

const refetchQueries = computed(() => {
  return [
    {
      query: InvoiceDetailQuery,
      variables: { filter: { idIn: [`ConsultantInvoice:${router.currentRoute.params.id}`] } },
    },
  ]
})

const getConfirmMessageAction = computed(() => {
  const map = {
    markAsPaid: "mark this invoice as paid",
    markAsProcessing: "mark this invoice as processing",
    archive: "archive this invoice",
    approve: "approve this invoice",
  }

  return map[invoiceAction.value as keyof typeof map]
})

const onConfirmAction = () => {
  switch (invoiceAction.value) {
    case actions.markAsPaid:
      onMarkAsPaid()
      break
    case actions.markAsProcessing:
      onMarkAsProcessing()
      break
    case actions.archive:
      onArchive()
      break
    case actions.approve:
      onApprove()
      break
    default:
      break
  }
}

const onApprove = async () => {
  if (!invoice.value?.approvals.length) {
    addError("This invoice cannot be approved because it has no approvals")
    return
  }

  isSubmitting.value = true

  await mutate<GrantApprovalMutationMutation, GrantApprovalMutationMutationVariables>({
    mutation: GrantApprovalMutation,
    variables: {
      approvalId: invoice.value.approvals[0].id,
    },
    refetchQueries: refetchQueries.value,
    handlePayloadErrors: false,
    done: () => {
      isSubmitting.value = false
    },
    onSuccess: () => {
      addSuccess("Invoice approved")
      onCloseDialog()
    },
    onError: (error) => {
      addError(error.message)
      console.log(error)
    },
  })
}

const onMarkAsPaid = async () => {
  if (!invoice.value?.id) return

  isSubmitting.value = true

  await mutate<
    MarkConsultantInvoiceAsPaidMutationMutation,
    MarkConsultantInvoiceAsPaidMutationMutationVariables
  >({
    mutation: MarkConsultantInvoiceAsPaidMutation,
    variables: {
      invoiceId: invoice.value.prn,
    },
    refetchQueries: refetchQueries.value,
    handlePayloadErrors: false,
    done: () => {
      isSubmitting.value = false
    },
    onSuccess: () => {
      addSuccess("Invoice marked as paid")
      onCloseDialog()
    },
    onError: (error) => {
      addError(error.message)
    },
  })
}

const onMarkAsProcessing = async () => {
  if (!invoice.value?.id) return

  isSubmitting.value = true

  await mutate<
    MarkConsultantInvoiceAsProcessingMutationMutation,
    MarkConsultantInvoiceAsProcessingMutationMutationVariables
  >({
    mutation: MarkConsultantInvoiceAsProcessingMutation,
    variables: {
      invoiceId: invoice.value.prn,
    },
    refetchQueries: refetchQueries.value,
    handlePayloadErrors: false,
    done: () => {
      isSubmitting.value = false
    },
    onSuccess: () => {
      addSuccess("Invoice marked as processing")
      onCloseDialog()
    },
    onMutationError: (error) => {
      addError(error.message)
    },
  })
}

const onArchive = async () => {
  if (!invoice.value?.id) return

  isSubmitting.value = true

  await mutate<
    ArchiveConsultantInvoiceMutationMutation,
    ArchiveConsultantInvoiceMutationMutationVariables
  >({
    mutation: ArchiveConsultantInvoiceMutation,
    variables: {
      invoiceId: invoice.value.id,
    },
    refetchQueries: refetchQueries.value,
    handlePayloadErrors: false,
    done: () => {
      isSubmitting.value = false
    },
    onSuccess: () => {
      addSuccess("Invoice archived")
      onCloseDialog()
    },
    onMutationError: (error) => {
      addError(error.message)
    },
  })
}

const onCloseDialog = () => {
  showDialog.value = false
  invoiceAction.value = null
}

const theme = {
  borderColor: "#179717",
  primaryColor: "#179717",
}

const showArchive = computed(() => ["pending", "approved"].includes(invoice.value?.state))
const showProcessing = computed(() => ["approved"].includes(invoice.value?.state))
const showPaid = computed(() => ["processing"].includes(invoice.value?.state))
const showPending = computed(() => ["pending"].includes(invoice.value?.state))

return { __sfc: true,t, router, mutate, pageState, isSubmitting, showDialog, invoiceAction, actions, onOpenDialog, addSuccess, addError, handlePageErrorState, loading, result, onError, invoice, refetchQueries, getConfirmMessageAction, onConfirmAction, onApprove, onMarkAsPaid, onMarkAsProcessing, onArchive, onCloseDialog, theme, showArchive, showProcessing, showPaid, showPending, ConsultantInvoiceDetail, PageState, PageStateError, Icon, AppDetailPage }
}

})